import Language, { getLanguageWithCode } from '@/entities/Language';
import Project from '@/entities/Project';
import {
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { auth, database } from './FirebaseProvider';

export interface RemoteProjectData {
  appId: string;
  mainLanguage?: string;
  languages?: string[];
  creatorId?: string;
}

export interface RemoteProject {
  id?: string;
  data: RemoteProjectData;
}

export const mapProject = (project: Project): RemoteProject => {
  let remoteProject: RemoteProject = {
    id: project.firestoreId,
    data: {
      appId: project.appId,
      mainLanguage: project.mainLanguage?.code,
      languages: project.languages?.map((language) => language.code),
    },
  };

  return remoteProject;
};

export const mapRemoteProject = (remoteProject: RemoteProject): Project => {
  let languages: Language[] = [];
  let mainLanguage: Language | undefined = undefined;
  remoteProject.data.languages?.forEach((languageCode) => {
    let language = getLanguageWithCode(languageCode);
    if (language) {
      if (
        remoteProject.data.mainLanguage &&
        language.code === remoteProject.data.mainLanguage
      ) {
        language.isMain = true;
        mainLanguage = language;
      }
      languages.push(language);
    }
  });

  return {
    firestoreId: remoteProject.id,
    appId: remoteProject.data.appId,
    mainLanguage: mainLanguage,
    languages: languages,
  };
};

export async function getRemoteProjectByAppId(
  appId: string,
): Promise<Project | null> {
  let project: Project | null = null;

  try {
    if (!appId) {
      return null;
    }

    const projectCollection = collection(database, 'project');
    const q = query(projectCollection, where('appId', '==', appId));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const data = querySnapshot.docs[0].data();
    const id = querySnapshot.docs[0].id;

    const remoteProject = {
      id: id,
      data: {
        appId: data.appId,
        mainLanguage: data.mainLanguage,
        languages: data.languages,
      },
    };

    if (remoteProject) {
      project = mapRemoteProject(remoteProject);
    }

    return project;
  } catch (error) {
    throw error;
  }
}

export async function updateRemoteProject(
  project: Project,
  updatedFields: (keyof RemoteProjectData)[],
): Promise<void> {
  try {
    const remoteProject = mapProject(project);
    if (remoteProject.id) {
      const projectDocRef = doc(database, 'project', remoteProject.id);

      let transformedField: any = {};
      updatedFields.forEach((updatedField) => {
        transformedField[updatedField] = remoteProject.data[updatedField];
      });

      await updateDoc(projectDocRef, transformedField);
    } else {
      throw new Error('Can not update project with empty firebase id');
    }
  } catch (error) {
    throw error;
  }
}

export async function createRemoteProject(project: Project): Promise<Project> {
  try {
    const projectDocRef = doc(collection(database, 'project'));
    const projectId = projectDocRef.id;
    const updatedProject = { ...project, firestoreId: projectId };
    const remoteProject = mapProject(updatedProject);
    remoteProject.data.creatorId = auth.currentUser?.uid;
    await setDoc(projectDocRef, remoteProject.data);
    return updatedProject;
  } catch (error) {
    throw error;
  }
}
